/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { SignJWT, importPKCS8 } from "jose";

import { Log } from "../logging/Log";
import { WebSocketError, WebSocketErrorCode } from "./types";

/**
 * JWT construction and signing service for WebSocket authentication.
 *
 * Replaces mTLS with application-layer JWT authentication on the web platform.
 * Uses the `jose` library for JWT generation with Web Crypto API underneath.
 *
 * **REQ-WEB-TRANS-02:** JWT-based authentication replacing mTLS.
 * **REQ-WEB-SEC-05:** JWT transmitted only over wss://, never logged or stored.
 */
export class WebSocketJwtService {
  private static readonly TAG: string = "WebSocketJwtService";

  /**
   * Default JWT validity window in seconds (REQ-WEB-TRANS-02 §4.6.3).
   */
  private static readonly DEFAULT_VALIDITY_SECONDS: number = 300;

  /**
   * Maximum allowed latency for crypto.subtle.sign() in milliseconds (REQ-WEB-PERF-06).
   */
  private static readonly SIGN_TIMEOUT_MS: number = 200;

  /**
   * Strip PEM armour and whitespace, returning the raw Base64 content.
   *
   * @param pem - PEM-encoded string (certificate or key)
   * @returns Base64-encoded DER content
   */
  private static pemToBase64(pem: string): string {
    return pem
      .replace(/-----BEGIN [^-]+-----/g, "")
      .replace(/-----END [^-]+-----/g, "")
      .replace(/\s+/g, "");
  }

  /**
   * Decode a DER length at the given offset, supporting both short-form
   * and long-form (0x81, 0x82, 0x83) lengths.
   *
   * @returns An object with the decoded `length` and the number of `bytesRead`
   *          (including the initial length byte). Returns null if the data is
   *          truncated or the form is unsupported.
   */
  private static decodeDerLength(
    bytes: Uint8Array,
    offset: number
  ): { length: number; bytesRead: number } | null {
    if (offset >= bytes.length) {
      return null;
    }
    const firstByte: number = bytes[offset];
    if (firstByte < 0x80) {
      // Short form: length fits in 7 bits
      return { length: firstByte, bytesRead: 1 };
    }
    const numLenBytes: number = firstByte & 0x7f;
    if (numLenBytes === 0 || numLenBytes > 3) {
      return null; // indefinite form or unsupported (> 3 bytes)
    }
    if (offset + 1 + numLenBytes > bytes.length) {
      return null; // truncated
    }
    let length: number = 0;
    for (let j: number = 0; j < numLenBytes; j++) {
      length = (length << 8) | bytes[offset + 1 + j];
    }
    return { length, bytesRead: 1 + numLenBytes };
  }

  /**
   * Encode an ASN.1 DER length value.
   */
  private static derEncodeLength(length: number): Uint8Array {
    if (length < 0x80) {
      return new Uint8Array([length]);
    }
    if (length < 0x100) {
      return new Uint8Array([0x81, length]);
    }
    if (length < 0x10000) {
      return new Uint8Array([0x82, (length >> 8) & 0xff, length & 0xff]);
    }
    return new Uint8Array([
      0x83,
      (length >> 16) & 0xff,
      (length >> 8) & 0xff,
      length & 0xff
    ]);
  }

  /**
   * Decode raw Base64 PEM content into a Uint8Array of DER bytes.
   */
  private static pemToDer(pem: string): Uint8Array {
    const base64: string = WebSocketJwtService.pemToBase64(pem);
    const binaryStr: string = atob(base64);
    const bytes: Uint8Array = new Uint8Array(binaryStr.length);
    for (let i: number = 0; i < binaryStr.length; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Encode DER bytes as a PEM string with the given label.
   */
  private static derToPem(der: Uint8Array, label: string): string {
    let binaryStr: string = "";
    for (let i: number = 0; i < der.length; i++) {
      binaryStr += String.fromCharCode(der[i]);
    }
    const base64: string = btoa(binaryStr);
    const lines: string[] = [];
    for (let i: number = 0; i < base64.length; i += 64) {
      lines.push(base64.substring(i, i + 64));
    }
    return (
      "-----BEGIN " +
      label +
      "-----\n" +
      lines.join("\n") +
      "\n-----END " +
      label +
      "-----"
    );
  }

  /**
   * Wrap an ASN.1 tag + content into a TLV (Tag-Length-Value) byte array.
   */
  private static derWrapTlv(tag: number, content: Uint8Array): Uint8Array {
    const lenBytes: Uint8Array = WebSocketJwtService.derEncodeLength(
      content.length
    );
    const result: Uint8Array = new Uint8Array(
      1 + lenBytes.length + content.length
    );
    result[0] = tag;
    result.set(lenBytes, 1);
    result.set(content, 1 + lenBytes.length);
    return result;
  }

  /**
   * Convert a PKCS#1 RSA private key to PKCS#8 format.
   *
   * PKCS#8 wraps PKCS#1 as:
   *   SEQUENCE { INTEGER 0, AlgorithmIdentifier { rsaEncryption, NULL }, OCTET STRING { pkcs1 } }
   */
  private static wrapRsaPkcs1InPkcs8(pkcs1Pem: string): string {
    const pkcs1Der: Uint8Array = WebSocketJwtService.pemToDer(pkcs1Pem);

    // Version INTEGER 0
    const version: Uint8Array = new Uint8Array([0x02, 0x01, 0x00]);

    // AlgorithmIdentifier SEQUENCE { OID rsaEncryption, NULL }
    const algorithmId: Uint8Array = new Uint8Array([
      0x30, 0x0d, 0x06, 0x09, 0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x01, 0x01,
      0x01, 0x05, 0x00
    ]);

    // OCTET STRING containing the PKCS#1 DER
    const octetString: Uint8Array = WebSocketJwtService.derWrapTlv(
      0x04,
      pkcs1Der
    );

    // Outer SEQUENCE
    const innerLen: number =
      version.length + algorithmId.length + octetString.length;
    const inner: Uint8Array = new Uint8Array(innerLen);
    inner.set(version, 0);
    inner.set(algorithmId, version.length);
    inner.set(octetString, version.length + algorithmId.length);

    const pkcs8Der: Uint8Array = WebSocketJwtService.derWrapTlv(0x30, inner);
    return WebSocketJwtService.derToPem(pkcs8Der, "PRIVATE KEY");
  }

  /**
   * Convert a SEC 1 EC private key to PKCS#8 format.
   *
   * Extracts the named-curve OID from the SEC 1 structure's
   * explicit [0] tag and wraps the whole SEC 1 blob in PKCS#8.
   */
  private static wrapEcSec1InPkcs8(ecPem: string): string {
    const sec1Der: Uint8Array = WebSocketJwtService.pemToDer(ecPem);

    // Scan for context-specific tag [0] EXPLICIT (0xa0) containing the curve OID
    let curveOidBytes: Uint8Array | null = null;
    for (let i: number = 0; i < sec1Der.length - 2; i++) {
      if (sec1Der[i] === 0xa0) {
        const lenResult = WebSocketJwtService.decodeDerLength(sec1Der, i + 1);
        if (lenResult === null) {
          continue;
        }
        const contentStart: number = i + 1 + lenResult.bytesRead;
        if (
          lenResult.length > 2 &&
          contentStart + lenResult.length <= sec1Der.length &&
          sec1Der[contentStart] === 0x06
        ) {
          const oidLen: number = sec1Der[contentStart + 1];
          curveOidBytes = sec1Der.slice(
            contentStart,
            contentStart + 2 + oidLen
          );
          break;
        }
      }
    }

    if (curveOidBytes === null) {
      throw new WebSocketError(
        WebSocketErrorCode.PRIVATE_KEY_MISSING,
        "Could not extract curve OID from EC private key"
      );
    }

    // Version INTEGER 0
    const version: Uint8Array = new Uint8Array([0x02, 0x01, 0x00]);

    // AlgorithmIdentifier SEQUENCE { OID ecPublicKey, OID curve }
    // ecPublicKey OID: 1.2.840.10045.2.1 → 2A 86 48 CE 3D 02 01
    const ecPublicKeyOid: Uint8Array = new Uint8Array([
      0x06, 0x07, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x02, 0x01
    ]);
    const algorithmIdContent: Uint8Array = new Uint8Array(
      ecPublicKeyOid.length + curveOidBytes.length
    );
    algorithmIdContent.set(ecPublicKeyOid, 0);
    algorithmIdContent.set(curveOidBytes, ecPublicKeyOid.length);
    const algorithmId: Uint8Array = WebSocketJwtService.derWrapTlv(
      0x30,
      algorithmIdContent
    );

    // OCTET STRING containing the SEC 1 DER
    const octetString: Uint8Array = WebSocketJwtService.derWrapTlv(
      0x04,
      sec1Der
    );

    // Outer SEQUENCE
    const innerLen: number =
      version.length + algorithmId.length + octetString.length;
    const inner: Uint8Array = new Uint8Array(innerLen);
    inner.set(version, 0);
    inner.set(algorithmId, version.length);
    inner.set(octetString, version.length + algorithmId.length);

    const pkcs8Der: Uint8Array = WebSocketJwtService.derWrapTlv(0x30, inner);
    return WebSocketJwtService.derToPem(pkcs8Der, "PRIVATE KEY");
  }

  /**
   * Ensure a private key PEM is in PKCS#8 format.
   *
   * The jose library's importPKCS8() requires PKCS#8
   * (`-----BEGIN PRIVATE KEY-----`). Keys from the API may arrive // gitleaks:allow
   * in PKCS#1 RSA or SEC 1 EC format, so we convert on the fly.
   */
  private static ensurePkcs8Pem(privateKeyPem: string): string {
    const trimmed: string = privateKeyPem.trim();

    // prettier-ignore
    if (trimmed.startsWith("-----BEGIN PRIVATE KEY-----")) { // gitleaks:allow
      return trimmed;
    }
    // prettier-ignore
    if (trimmed.startsWith("-----BEGIN RSA PRIVATE KEY-----")) { // gitleaks:allow
      Log.d(
        WebSocketJwtService.TAG,
        "ensurePkcs8Pem(): Converting PKCS#1 RSA → PKCS#8"
      );
      return WebSocketJwtService.wrapRsaPkcs1InPkcs8(trimmed);
    }
    // prettier-ignore
    if (trimmed.startsWith("-----BEGIN EC PRIVATE KEY-----")) { // gitleaks:allow
      Log.d(
        WebSocketJwtService.TAG,
        "ensurePkcs8Pem(): Converting SEC 1 EC → PKCS#8"
      );
      return WebSocketJwtService.wrapEcSec1InPkcs8(trimmed);
    }

    // Unknown format — return as-is and let importPKCS8 report the error
    Log.w(
      WebSocketJwtService.TAG,
      "ensurePkcs8Pem(): Unrecognised PEM header, passing through"
    );
    return trimmed;
  }

  /**
   * Extract the Common Name (CN) from an X.509 certificate PEM string.
   *
   * Parses the certificate's subject field without requiring a full
   * ASN.1 parser — decodes the base64 DER, scans for the OID 2.5.4.3
   * (id-at-commonName), and reads the UTF8String/PrintableString value
   * that follows.
   *
   * @param certificatePem - PEM-encoded X.509 certificate
   * @returns The Common Name string
   * @throws WebSocketError if CN cannot be extracted
   */
  public static extractCommonName(certificatePem: string): string {
    try {
      const base64: string = WebSocketJwtService.pemToBase64(certificatePem);
      const binaryStr: string = atob(base64);
      const bytes: Uint8Array = new Uint8Array(binaryStr.length);
      for (let i: number = 0; i < binaryStr.length; i++) {
        bytes[i] = binaryStr.charCodeAt(i);
      }

      // OID 2.5.4.3 (id-at-commonName) encoded as DER: 55 04 03
      // In X.509 the Issuer CN appears before the Subject CN, so we
      // must collect all CN values and return the last one (Subject).
      const cnOid: number[] = [0x55, 0x04, 0x03];
      let lastCn: string | null = null;
      for (let i: number = 0; i < bytes.length - cnOid.length; i++) {
        if (
          bytes[i] === cnOid[0] &&
          bytes[i + 1] === cnOid[1] &&
          bytes[i + 2] === cnOid[2]
        ) {
          // After the OID, there's a tag byte (0x0C=UTF8String, 0x13=PrintableString, etc.)
          // followed by a DER length, then the value.
          const tagIndex: number = i + cnOid.length;
          const tag: number = bytes[tagIndex];
          if (tag === 0x0c || tag === 0x13 || tag === 0x16 || tag === 0x1e) {
            const lenResult = WebSocketJwtService.decodeDerLength(
              bytes,
              tagIndex + 1
            );
            if (lenResult !== null) {
              const valueStart: number = tagIndex + 1 + lenResult.bytesRead;
              const valueBytes: Uint8Array = bytes.slice(
                valueStart,
                valueStart + lenResult.length
              );
              lastCn = new TextDecoder().decode(valueBytes);
            }
          }
        }
      }

      if (lastCn === null) {
        throw new Error("Common Name OID not found in certificate");
      }
      return lastCn;
    } catch (error: unknown) {
      Log.e(
        WebSocketJwtService.TAG,
        "extractCommonName(): Failed to parse certificate",
        error
      );
      throw new WebSocketError(
        WebSocketErrorCode.CERTIFICATE_PARSE_ERROR,
        "Failed to extract Common Name from certificate"
      );
    }
  }

  /**
   * Detect the signing algorithm from the certificate's public key.
   *
   * Scans the DER-encoded certificate for known algorithm OIDs:
   * - RSA (1.2.840.113549.1.1.1) → RS256
   * - EC P-256 (1.2.840.10045.3.1.7) → ES256
   * - EC P-384 (1.3.132.0.34) → ES384
   *
   * @param certificatePem - PEM-encoded X.509 certificate
   * @returns The jose-compatible algorithm identifier
   */
  public static detectAlgorithm(certificatePem: string): string {
    const base64: string = WebSocketJwtService.pemToBase64(certificatePem);
    const binaryStr: string = atob(base64);
    const bytes: Uint8Array = new Uint8Array(binaryStr.length);
    for (let i: number = 0; i < binaryStr.length; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }

    // RSA OID: 1.2.840.113549.1.1.1 → 2A 86 48 86 F7 0D 01 01 01
    const rsaOid: number[] = [
      0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x01, 0x01, 0x01
    ];
    // EC P-256 OID: 1.2.840.10045.3.1.7 → 2A 86 48 CE 3D 03 01 07
    const ecP256Oid: number[] = [
      0x2a, 0x86, 0x48, 0xce, 0x3d, 0x03, 0x01, 0x07
    ];
    // EC P-384 OID: 1.3.132.0.34 → 2B 81 04 00 22
    const ecP384Oid: number[] = [0x2b, 0x81, 0x04, 0x00, 0x22];

    const containsOid = (oid: number[]): boolean => {
      for (let i: number = 0; i <= bytes.length - oid.length; i++) {
        let match: boolean = true;
        for (let j: number = 0; j < oid.length; j++) {
          if (bytes[i + j] !== oid[j]) {
            match = false;
            break;
          }
        }
        if (match) {
          return true;
        }
      }
      return false;
    };

    if (containsOid(ecP256Oid)) {
      return "ES256";
    }
    if (containsOid(ecP384Oid)) {
      return "ES384";
    }
    if (containsOid(rsaOid)) {
      return "RS256";
    }

    // Default to RS256 per the reference implementation
    Log.w(
      WebSocketJwtService.TAG,
      "detectAlgorithm(): Could not detect algorithm OID, defaulting to RS256"
    );
    return "RS256";
  }

  /**
   * Create and sign a JWT for WebSocket authentication.
   *
   * The JWT is created immediately before the WebSocket connection attempt
   * and MUST NOT be reused across attempts (REQ-WEB-TRANS-02 §4.6.3).
   *
   * @param certificatePem - PEM-encoded X.509 certificate
   * @param privateKeyPem - PEM-encoded private key (PKCS#8, PKCS#1, or SEC 1)
   * @param validitySeconds - JWT validity window in seconds (default: 300)
   * @returns Signed JWT string
   * @throws WebSocketError on missing inputs, signing failure, or timeout
   */
  public static async createJwt(
    certificatePem: string,
    privateKeyPem: string,
    validitySeconds: number = WebSocketJwtService.DEFAULT_VALIDITY_SECONDS
  ): Promise<string> {
    if (!certificatePem) {
      throw new WebSocketError(
        WebSocketErrorCode.CERTIFICATE_MISSING,
        "Certificate PEM is required for JWT construction"
      );
    }
    if (!privateKeyPem) {
      throw new WebSocketError(
        WebSocketErrorCode.PRIVATE_KEY_MISSING,
        "Private key PEM is required for JWT construction"
      );
    }

    try {
      const algorithm: string =
        WebSocketJwtService.detectAlgorithm(certificatePem);
      const subject: string =
        WebSocketJwtService.extractCommonName(certificatePem);
      const x5c: string[] = [WebSocketJwtService.pemToBase64(certificatePem)];

      Log.d(
        WebSocketJwtService.TAG,
        `createJwt(): algorithm=${algorithm}, subject length=${subject.length}`
      );

      // Ensure the key is in PKCS#8 format (convert from PKCS#1 / SEC 1 if needed)
      const pkcs8Pem: string =
        WebSocketJwtService.ensurePkcs8Pem(privateKeyPem);

      // Import the private key using jose (handles PKCS#8 PEM natively)
      const privateKey: CryptoKey = await importPKCS8(pkcs8Pem, algorithm);

      const now: number = Math.floor(Date.now() / 1000);

      // Build and sign the JWT with a timeout guard (REQ-WEB-PERF-06)
      const jwtPromise: Promise<string> = new SignJWT({
        sub: subject
      })
        .setProtectedHeader({
          alg: algorithm,
          typ: "JWT",
          x5c: x5c
        })
        .setNotBefore(now - validitySeconds)
        .setExpirationTime(now + validitySeconds)
        .sign(privateKey);

      const jwt: string = await Promise.race([
        jwtPromise,
        new Promise<never>((_, reject) =>
          setTimeout(
            () =>
              reject(
                new WebSocketError(
                  WebSocketErrorCode.JWT_SIGNING_ERROR,
                  `JWT signing exceeded ${WebSocketJwtService.SIGN_TIMEOUT_MS}ms timeout`
                )
              ),
            WebSocketJwtService.SIGN_TIMEOUT_MS
          )
        )
      ]);

      Log.d(WebSocketJwtService.TAG, "createJwt(): JWT created successfully");

      return jwt;
    } catch (error: unknown) {
      if (error instanceof WebSocketError) {
        throw error;
      }
      Log.e(WebSocketJwtService.TAG, "createJwt()", error);
      throw new WebSocketError(
        WebSocketErrorCode.JWT_SIGNING_ERROR,
        `JWT construction failed: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }
}
